import { useState, FormEvent } from "react";
import { submitForm } from "@/lib/submitForm";

const kitchenTypes = [
  "Restaurant / Café",
  "Hotel / Lodge",
  "Hospital / Clinic",
  "School / University",
  "Bakery / Confectionery",
  "Catering Company",
  "Supermarket / Deli",
  "Food Processing Plant",
  "Other",
];

export default function KitchenInquiry() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await submitForm("commercial-kitchen", data);
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <div>
            <span className="text-teal-600 text-xs font-semibold uppercase tracking-widest mb-3 block">Let&apos;s Talk</span>
            <h2 className="text-gray-900 text-4xl md:text-5xl font-extrabold uppercase tracking-tight mb-6">
              Get a Free<br />
              <span className="text-teal-600">Kitchen Quote</span>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-10">
              Fill in the form and one of our kitchen specialists will contact you within 24 hours to discuss your requirements, provide a detailed quote, and schedule a site visit.
            </p>

            <div className="space-y-6">
              {[
                { icon: "ri-phone-line", label: "Call Us Directly", value: "072 411 4555", href: "tel:0724114555" },
                { icon: "ri-mail-line", label: "Send an Email", value: "info@isman.co.ke", href: "mailto:info@isman.co.ke" },
                { icon: "ri-map-pin-line", label: "Visit Our Workshop", value: "Nairobi, Kenya", href: "#" },
                { icon: "ri-time-line", label: "Business Hours", value: "Mon–Sat, 8:00 AM – 6:00 PM", href: "#" },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-600 transition-colors duration-300 shrink-0">
                    <i className={`${item.icon} text-lg text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">{item.label}</p>
                    <p className="text-gray-800 text-sm font-semibold group-hover:text-teal-600 transition-colors">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Trust badges */}
            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-wrap gap-4">
              {["15+ Years Experience", "500+ Kitchens Installed", "Food-Grade Materials", "Certified Engineers"].map((badge) => (
                <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                  <i className="ri-check-line text-teal-600"></i>
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="bg-gray-50 rounded-lg p-8 border border-gray-100">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-xl mb-2 uppercase tracking-wide">Inquiry Received!</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Thank you! One of our kitchen specialists will be in touch within 24 hours to discuss your project.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-teal-600 hover:text-teal-700 text-sm font-semibold underline cursor-pointer"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-isman-form
                className="space-y-5"
              >
                <h3 className="text-gray-900 font-bold text-sm uppercase tracking-widest mb-6">Project Details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Kamau"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-400 bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="0712 345 678"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-400 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-400 bg-white"
                  />
                </div>

                <div>
                  <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">Type of Business *</label>
                  <select
                    name="business_type"
                    required
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                  >
                    <option value="">Select business type...</option>
                    {kitchenTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">Location / City *</label>
                  <input
                    type="text"
                    name="location"
                    required
                    placeholder="Nairobi, Westlands"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-400 bg-white"
                  />
                </div>

                <div>
                  <label className="text-gray-500 text-xs uppercase tracking-wide font-semibold block mb-1.5">
                    Project Details
                    <span className="ml-2 text-gray-400 normal-case font-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="project_details"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your kitchen size, equipment needs, timeline..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-400 bg-white resize-none"
                  />
                  {charCount > 500 && (
                    <p className="text-red-500 text-xs mt-1">Maximum 500 characters allowed.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-bold py-3 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? "Sending..." : "Submit Inquiry — Free Quote"}
                </button>
                <p className="text-gray-400 text-xs text-center">
                  No commitment required. We respond within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
